/**
 * +ranger-specs: bot kits for the Ranger specialisations (docs/design/classes/specs.md "Ranger", the
 * rotation sections). The APL presses evolved ids directly (the spec'd ClassDef binds them). Auto,
 * Group Finder strangers and the harness all play these.
 *
 *  - Deadeye: a reflex hits the perfect-reload window at a skill-based rate (rolled once per reload:
 *    a miss either jams early or lets the reload run); Hammer Spray when the cylinder is full and no
 *    the cylinder is full (always into a pack), Ricochet / Flashbang on cooldown, Quickdraw fills.
 *  - Grovewarden: its own heal planner (healPlan): Burstbloom for spikes, Overgrowth Surge / Worldroot
 *    for group damage, Lifevine on the tank (yourself when alone), Budding rolled on everyone; with
 *    nothing to heal it Thorn Whips (damage that still buds the most injured).
 *  - Beastbound: Stand Guard alone (the beast tanks), Hunt in a group; Fanged Maul, Spirit Snare and
 *    Spirit Herd on cooldown, Pack Fury at 50 Bond, Mark Prey fills.
 */
import type { Command, UnitId } from '@keystone/sim';
import { BASE_AURA, BEASTBOUND, BEASTBOUND_AURA, DEADEYE, DEADEYE_AURA, DEADEYE_RELOAD, GROVEWARDEN, GROVEWARDEN_AURA, GROVEWARDEN_HOTS } from '@keystone/content';
import type { ClassKit } from '../kits';
import { rollFor, type BotMemory } from '../memory';
import { abilityLocked, hpPct, type Perception, type RUnit } from '../perception';
import { dist } from '../geometry';

const DE = DEADEYE;
const DEA = DEADEYE_AURA;
const GW = GROVEWARDEN;
const GWA = GROVEWARDEN_AURA;
const BB = BEASTBOUND;
const BBA = BEASTBOUND_AURA;

/** A fight worth a cooldown: a sturdy target or a pack. */
const bigFight = { type: 'any' as const, of: [{ type: 'targetHpAbove' as const, pct: 0.5 }, { type: 'targets' as const, atLeast: 3, radius: 10 }] };

/** The bot presses `abilityId` (on `targetId`) if the sim allows it now. */
function press(p: Perception, abilityId: string, targetId: UnitId | null = null): Command[] | null {
  if (abilityLocked(p.view, p.self, abilityId)) return null;
  if (!p.view.queries.canCast(p.self.id, abilityId, targetId, null).ok) return null;
  return [{ type: 'cast', actorId: p.self.id, abilityId, ...(targetId ? { targetId } : {}) }];
}

// ---------------------------------------------------------------------------
// Deadeye
// ---------------------------------------------------------------------------

/**
 * How often a bot lands the perfect reload: its timing skill (kick reliability: novice 0.6,
 * competent 0.85, expert 0.97), clamped. Rolled once per reload (deterministic per bot seed).
 */
export function perfectReloadChance(mem: BotMemory): number {
  return Math.max(0.2, Math.min(0.97, mem.skill.kickReliability));
}

/**
 * The reload reflex: when to press Snap Shut (key 4) during a reload, in ms after it started, or null
 * to let it run. A hit aims inside the window (with a margin), a miss jams early half the time.
 */
export function reloadPressAt(mem: BotMemory, reloadIid: string): number | null {
  const r = rollFor(mem, `reload:${reloadIid}`, perfectReloadChance(mem), 0, 1);
  // A press lands up to one think (100 ms) + one tick after it is decided: aim early in the window.
  const lo = DEADEYE_RELOAD.perfectFromMs + 30;
  const hi = DEADEYE_RELOAD.perfectToMs - 180;
  if (r.ok) return lo + r.at * (hi - lo);
  return r.at < 0.5 ? 250 + r.at * 500 : null;
}

/** The command reaches the sim one tick after the think that decides it. */
const DECIDE_LAG_MS = 50;

function deadeyeReflex(p: Perception, mem: BotMemory): Command[] | null {
  const inst = p.self.auras.find((a) => a.auraId === DEA.reloading);
  if (!inst) return null;
  const at = reloadPressAt(mem, inst.iid);
  if (at === null || p.now + DECIDE_LAG_MS - inst.appliedAt < at) return null;
  return press(p, DE.rollReload); // key 4 is Snap Shut while reloading
}

export const deadeyeKit: ClassKit = {
  classId: 'ranger',
  role: 'dps',
  ranged: true,
  interrupt: DE.disarmingShot,
  reflex: deadeyeReflex,
  oocBuffs: [{ ability: DE.trailStride, when: [{ type: 'selfLacksAura', auraId: BASE_AURA.swiftfoot }, { type: 'any', of: [{ type: 'targetDistAbove', yd: 30 }, { type: 'moving' }] }] }],
  apl: [
    { ability: DE.highNoon, when: [{ type: 'inCombat' }, bigFight] },
    // Roll out of melee when hurt (it reloads on the way).
    { ability: DE.rollReload, when: [{ type: 'inCombat' }, { type: 'enemiesNearSelf', atLeast: 1, radius: 5 }, { type: 'selfHpBelow', pct: 0.8 }, { type: 'selfLacksAura', auraId: DEA.reloading }] },
    { ability: DE.flashbang, when: [{ type: 'inCombat' }] },
    // Trail Stride in combat: a sure crit (an Explosive Round) on cooldown.
    { ability: DE.trailStride, when: [{ type: 'inCombat' }, { type: 'selfLacksAura', auraId: DEA.steadyHand }] },
    // Hammer Spray: into a pack with 3+ rounds; on one target with a full cylinder (5+).
    { ability: DE.fanTheHammer, when: [{ type: 'targets', atLeast: 3, radius: 10 }, { type: 'resource', resource: 'rounds', atLeast: 3 }] },
    { ability: DE.fanTheHammer, when: [{ type: 'resource', resource: 'rounds', atLeast: 5 }] },
    { ability: DE.ricochet },
    { ability: DE.quickdraw },
  ],
};

// ---------------------------------------------------------------------------
// Grovewarden
// ---------------------------------------------------------------------------

const RANGE = 40;
/** Budding is refreshed inside its pandemic window. */
const REFRESH_MS = 4500;

function mine(u: RUnit, auraId: string, me: UnitId): { until: number } | null {
  const a = u.auras.find((x) => x.auraId === auraId && x.casterId === me);
  return a ? { until: a.expiresAt === null ? Infinity : a.expiresAt } : null;
}

function grovewardenHeal(p: Perception, mem: BotMemory): Command[] | null {
  void mem;
  const self = p.self;
  const now = p.now;
  const inCombat = p.engaged.length > 0;
  const near = p.party.filter((u) => u.alive && dist(u.pos, self.pos) <= RANGE);
  const hurt = (below: number) => near.filter((u) => hpPct(u) < below).length;
  const byHp = [...near].sort((a, b) => hpPct(a) - hpPct(b) || (a.id < b.id ? -1 : 1));
  const hasHot = (u: RUnit) => GROVEWARDEN_HOTS.some((id) => !!mine(u, id, self.id));
  const group = near.length >= 2;

  // 1. Worldroot when the group sinks.
  if (inCombat && (hurt(0.6) >= 2 || (!group && hpPct(self) < 0.4))) {
    const c = press(p, GW.worldroot);
    if (c) return c;
  }
  // 2. Burstbloom: a spike on someone who carries my growth.
  for (const u of byHp) {
    if (hpPct(u) >= 0.55) break;
    if (!hasHot(u)) continue;
    const c = press(p, GW.burstbloom, u.id);
    if (c) return c;
  }
  // 3. Overgrowth Surge: group damage (Budding on everyone at once).
  if (inCombat && (hurt(0.85) >= 3 || (near.length <= 2 && hurt(0.8) >= near.length) || (group && hurt(0.9) >= near.length))) {
    const c = press(p, GW.overgrowthSurge);
    if (c) return c;
  }
  // 4. Lifevine on the tank (yourself when alone, else the most injured), refreshed as it runs out.
  const vineOn = p.tank?.alive && near.includes(p.tank) ? p.tank : !group ? self : byHp[0];
  if (vineOn && (inCombat || hpPct(vineOn) < 0.9)) {
    const v = mine(vineOn, GWA.lifevine, self.id);
    if (!v || v.until - now < 1500) {
      const c = press(p, GW.lifevine, vineOn.id);
      if (c) return c;
    }
  }
  // 5. Budding rolled on everyone in a fight (the injured first); out of a fight on the injured only.
  for (const u of byHp) {
    if (!inCombat && hpPct(u) >= 0.9) continue;
    if (inCombat && !group && hpPct(u) >= 0.98) continue;
    const b = mine(u, GWA.budding, self.id);
    if (b && b.until - now > REFRESH_MS) continue;
    const c = press(p, GW.budding, u.id);
    if (c) return c;
  }
  // 6. Grove Stride: one more HoT on everyone close (off the GCD).
  if (inCombat && group && hurt(0.95) >= 2) {
    const c = press(p, GW.groveStride);
    if (c) return c;
  }
  // 7. Never sit on two Burstbloom charges while someone is hurt and growing.
  for (const u of byHp) {
    if (hpPct(u) >= 0.8) break;
    if (!hasHot(u)) continue;
    const cd = self.cooldowns[GW.burstbloom];
    if (cd && cd.charges < cd.maxCharges) break;
    const c = press(p, GW.burstbloom, u.id);
    if (c) return c;
  }
  return null;
}

export const grovewardenKit: ClassKit = {
  classId: 'ranger',
  role: 'healer',
  ranged: true,
  interrupt: GW.thornpin,
  groupHeal: GW.overgrowthSurge,
  heals: { manaResource: 'mana' },
  healPlan: grovewardenHeal,
  apl: [
    { ability: GW.snarlroot, when: [{ type: 'inCombat' }, { type: 'enemiesNearSelf', atLeast: 1, radius: 7 }] },
    { ability: GW.wildstride, when: [{ type: 'inCombat' }, { type: 'enemiesNearSelf', atLeast: 1, radius: 5 }, { type: 'selfHpBelow', pct: 0.7 }] },
    // Alone (Auto): keep growth on yourself in a fight, then whip.
    { ability: GW.lifevine, target: 'self', when: [{ type: 'inCombat' }, { type: 'not', cond: { type: 'partySize', atLeast: 2 } }, { type: 'selfHpBelow', pct: 0.85 }, { type: 'selfLacksAura', auraId: GWA.lifevine }] },
    // Thorn Whip: Budding on an enemy (damage, and it buds the most injured ally).
    { ability: GW.budding, target: 'enemy', when: [{ type: 'inCombat' }] },
  ],
};

// ---------------------------------------------------------------------------
// Beastbound
// ---------------------------------------------------------------------------

/** Alone (no group): the beast should guard (tank); in a group it hunts beside the tank. */
const alone = { type: 'not' as const, cond: { type: 'partySize' as const, atLeast: 2 } };
const inGroup = { type: 'partySize' as const, atLeast: 2 };

export const beastboundKit: ClassKit = {
  classId: 'ranger',
  role: 'dps',
  ranged: true,
  interrupt: BB.howlShot,
  oocBuffs: [
    // The command first (E is Stand Guard while hunting, Hunt while guarding).
    { ability: BB.guard, when: [alone, { type: 'selfLacksAura', auraId: BBA.guarding }] },
    { ability: BB.guard, when: [inGroup, { type: 'selfAura', auraId: BBA.guarding }] },
    { ability: BB.packStride, when: [{ type: 'selfLacksAura', auraId: BASE_AURA.swiftfoot }, { type: 'any', of: [{ type: 'targetDistAbove', yd: 30 }, { type: 'moving' }] }] },
  ],
  apl: [
    { ability: BB.guard, when: [alone, { type: 'selfLacksAura', auraId: BBA.guarding }] },
    { ability: BB.guard, when: [inGroup, { type: 'selfAura', auraId: BBA.guarding }] },
    { ability: BB.spiritMerge, when: [{ type: 'inCombat' }, bigFight] },
    { ability: BB.boundLeap, when: [{ type: 'inCombat' }, { type: 'enemiesNearSelf', atLeast: 1, radius: 5 }, { type: 'selfHpBelow', pct: 0.8 }] },
    { ability: BB.maul, when: [{ type: 'inCombat' }] },
    // Pack Stride mid-fight: a second Fanged Maul.
    { ability: BB.packStride, when: [{ type: 'inCombat' }, { type: 'not', cond: { type: 'cooldownBelow', ability: BB.maul, ms: 4000 } }] },
    { ability: BB.packFury, when: [{ type: 'resource', resource: 'bond', atLeast: 50 }] },
    { ability: BB.packFury, when: [{ type: 'selfAura', auraId: BBA.kindred }, { type: 'resource', resource: 'bond', atLeast: 25 }] },
    { ability: BB.spiritSnare, when: [{ type: 'inCombat' }] },
    { ability: BB.spiritHerd, when: [{ type: 'inCombat' }] },
    { ability: BB.markPrey },
  ],
};

/** Spec id → kit. */
export const RANGER_SPEC_KITS: Readonly<Record<string, ClassKit>> = { deadeye: deadeyeKit, grovewarden: grovewardenKit, beastbound: beastboundKit };
